/**
 * @module useSalesForm
 * Shared form state for the manual sales entry dialog.
 *
 * Business rules (loaded from the API) drive three cascade behaviours:
 * 1. Selecting a city auto-selects the location if only one is available
 * 2. Selecting a location+brand auto-selects the platform if only one is configured
 * 3. The platform dropdown only shows platforms that are valid for the current
 *    location+brand combination (no invalid entries visible to the user)
 *
 * The form is a singleton (`reactive` at module scope) so state is preserved
 * when the user switches away and returns to the dialog without closing it.
 * `resetFormState()` must be called explicitly on close/cancel.
 *
 * Color functions (cityColor, locationColor, brandColor) provide consistent
 * visual coding across the sales table views — UAE cities use green for Dubai
 * and yellow for Al Ain.
 */

import { ref, reactive, computed, onMounted } from "vue";
import api from "@/utils/api.js";

// ── Form state ────────────────────────────────────────────────────────────────

const form = reactive({
  country: "UAE",
  city: null,
  location: null,
  brand: null,
  platform: null,
  amount: null,
  orders: null,
  sales_date: new Date().toISOString().split("T")[0],
});

const error = ref("");
const message = ref("");
const loading = ref(false);
const duplicateWarning = ref(false);  // True when the backend detects an existing record for this date/location/brand/platform
const existingSaleId = ref(null);     // ID of the duplicate, used to offer an "update instead" action
const editingId = ref(null);          // ID of the record being edited (null = create mode)
const recentSales = ref([]);
const currentSaleReporter = ref(""); // Email of the user who originally entered the record
const userEmail = ref("");

// ── Dialog state ──────────────────────────────────────────────────────────────

const dialogOpen = ref(false);
const hideRecentSales = ref(false);  // Hidden when opened from Raw Data view (space is limited)
const sourceComponent = ref("");     // "home" | "rawdata" — affects dialog layout

const isEditMode = computed(() => editingId.value !== null);

// ── Business rules ────────────────────────────────────────────────────────────

// Populated from /api/accounting/sales/business-rules on first mount.
// Empty until loaded — filteredLocations and filteredPlatforms return [] safely.
const cityLocationMap = reactive({});
const locationBrandPlatformMap = reactive({});
const loadingRules = ref(false);
const rulesError = ref("");

/** Fetch the hierarchical business rules map from the sales service. */
const loadBusinessRules = async () => {
  loadingRules.value = true;
  rulesError.value = "";

  try {
    const { data } = await api.accountingApi.get(`/api/accounting/sales/business-rules`);
    Object.assign(cityLocationMap, data.cityLocationMap || {});
    Object.assign(locationBrandPlatformMap, data.locationBrandPlatformMap || {});
    return true;
  } catch (error) {
    console.error("Failed to load business rules:", error);
    rulesError.value = error.message;
    return false;
  } finally {
    loadingRules.value = false;
  }
};

// ── Cascading filter computeds ────────────────────────────────────────────────

/**
 * Locations available for the selected city.
 * Auto-selects when only one option exists — saves a click for single-location cities.
 */
const filteredLocations = computed(() => {
  if (!form.city) return [];
  if (cityLocationMap[form.city].length == 1) {
    form.location = cityLocationMap[form.city][0].name;
  }
  return cityLocationMap[form.city] || [];
});

/**
 * Platforms valid for the selected location+brand combination.
 * Only platforms configured in business rules are shown — prevents invalid entries
 * that would be rejected by the backend validation.
 */
const filteredPlatforms = computed(() => {
  if (!form.location || !form.brand) return [];
  if (locationBrandPlatformMap[form.location][form.brand].length == 1) {
    form.platform = locationBrandPlatformMap[form.location][form.brand][0].name;
  }
  return locationBrandPlatformMap[form.location][form.brand] || [];
});

// Reverse lookup: given a location name, find its parent city.
// Used by locationColor to apply the city-based colour without needing the city in scope.
const locationToCityMap = {};
Object.entries(cityLocationMap).forEach(([city, locations]) => {
  locations.forEach((location) => {
    locationToCityMap[location.name] = city;
  });
});

// ── Row color functions ───────────────────────────────────────────────────────
// Applied to city/location/brand cells in the sales matrix tables for quick
// visual grouping of data by geography and brand.

function cityColor(city) {
  switch (city) {
    case "Dubai":
      return "text-green-darken-2";
    case "Al Ain":
      return "text-yellow-darken-2";
    default:
      return null;
  }
}

function locationColor(location) {
  const city = locationToCityMap[location];
  switch (city) {
    case "Dubai":
      return "text-green-lighten-1";
    case "Al Ain":
      return "text-yellow";
    default:
      return null;
  }
}

function brandColor(brand) {
  switch (brand) {
    case "Felice":
      return "text-indigo-lighten-2";
    case "Fusion":
      return "text-indigo-lighten-3";
    case "Little Italy":
      return "text-indigo-lighten-4";
    default:
      return null;
  }
}

// ── Form helpers ──────────────────────────────────────────────────────────────

const clearForm = () => {
  Object.assign(form, {
    country: "UAE",
    city: null,
    location: null,
    brand: null,
    platform: null,
    amount: null,
    orders: null,
    sales_date: new Date().toISOString().split("T")[0],
  });
};

/** Open the dialog pre-populated with an existing record's data for editing. */
const openEditDialog = (record, fromComponent = "rawdata") => {
  if (record) {
    form.country = record.country || "UAE";
    form.city = record.city;
    form.location = record.location;
    form.brand = record.brand;
    form.platform = record.platform;
    form.amount = record.amount;
    form.orders = record.orders;
    form.sales_date = record.sales_date;

    editingId.value = record.id;
    currentSaleReporter.value = record.reporter_email;
    duplicateWarning.value = false;

    dialogOpen.value = true;
    sourceComponent.value = fromComponent;
    // Hide the recent-sales panel when editing from Raw Data — the table context already shows it
    hideRecentSales.value = fromComponent === "rawdata";
  }
};

const closeDialog = () => {
  dialogOpen.value = false;
  resetFormState();
  sourceComponent.value = "";
  hideRecentSales.value = false;
};

const resetFormState = () => {
  editingId.value = null;
  currentSaleReporter.value = "";
  error.value = "";
  message.value = "";
  duplicateWarning.value = false;
  existingSaleId.value = null;

  clearForm();
};

/** Called after a successful save/delete to close the dialog and return the action type. */
const completeEdit = (action = "update") => {
  dialogOpen.value = false;
  resetFormState();
  return action;
};

// ── Composable export ─────────────────────────────────────────────────────────

export function useSalesForm() {
  // Load business rules when the composable is first mounted by a component
  onMounted(async () => {
    await loadBusinessRules();
  });

  return {
    loadingRules,
    rulesError,
    loadBusinessRules,

    filteredPlatforms,
    locationBrandPlatformMap,
    locationToCityMap,

    // Row color functions
    cityColor,
    locationColor,
    brandColor,

    clearForm,
    filteredLocations,
    form,
    error,
    message,
    loading,
    duplicateWarning,
    existingSaleId,
    editingId,
    recentSales,
    currentSaleReporter,
    userEmail,
    isEditMode,

    // Dialog states
    dialogOpen,
    hideRecentSales,
    sourceComponent,
    openEditDialog,
    closeDialog,
    resetFormState,
    completeEdit,
  };
}
